import React, { useState, useEffect, useMemo, useCallback } from "react";

import { PageStandard } from "components/admin/PageStandard";
import {
  Grid,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { fetchConsumerMonitoring } from "libLegacy/api/dashboard";

import {
  SelectorGranularities,
  SelectorGranularitiesProps,
} from "components/admin/SelectorGranularities";
import { BlockStandard } from "components/admin/BlockStandard";
import { InfoCard } from "components/admin/InfoCard";
import { findMonth, findWeek, findYear } from "logic/DateSelectors";
import {
  ConsumerMonitoringData,
  ConsumerMonitoringUnits,
} from "libLegacy/api/models";
import CustomSnackbar from "components/libComponents/CustomSnackbar";
import { ptBR } from "date-fns/locale";
import { ChartConsumerMonitoring } from "components/charts/ChartConsumerMonitoring";
import { useToken, useUserContext } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";
import { AutomationSpace } from "api/automationTypes";

const PAGE_DATA = {
  title: "Monitoramento",
  subtitle: "Veja o seu consumo de energia em tempo real",
  displaySelector: true,
};

const PAGE_TABS = [
  {
    label: "diário",
    value: "day",
  },
  {
    label: "semanal",
    value: "week",
  },
  {
    label: "mensal",
    value: "month",
  },
  {
    label: "anual",
    value: "year",
  },
];

const useStyles = makeStyles()((theme) => ({
  toggleButton: {
    "&&.MuiToggleButton-root": {
      borderRadius: "20px",
      border: `1px solid ${theme.palette.secondary.main}`,
      padding: theme.spacing(0.5),
      height: "max-content",
      textTransform: "none",

      "&:not(:first-child)": {
        marginLeft: theme.spacing(2),
      },

      "&.Mui-selected": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,

        "& > span": {
          color: theme.palette.common.white,
        },
      },
    },
  },
}));

const UNIT_CONFIG = {
  kWh: {
    style: "decimal",
  },
  money: {
    style: "currency",
    currency: "BRL",
  },
  C02e: {
    style: "decimal",
  },
};

const TODAY = new Date();

export const ConsumerMonitoring = ({ history }) => {
  const token = useToken();
  const [referenceDate, setReferenceDate] = useState<{
    granularity: SelectorGranularitiesProps["granularity"];
    start: Date;
    end?: Date;
  }>({ granularity: "day", start: TODAY });
  const [units, setUnits] = useState<ConsumerMonitoringUnits>("kWh");
  const [data, setData] = useState<ConsumerMonitoringData>([]);
  const [error, setError] = useState<string>("");
  const { classes } = useStyles();
  const { currentSpace } = useUserContext();
  const setGlobalError = useSetError();

  const dataOverview = useMemo(
    () => data.filter((item) => item.name === "Geral")[0] || {},
    [data]
  );

  useEffect(() => {
    const loadData = async () => {
      const { start, end, granularity } = referenceDate;
      setData([]);
      try {
        const newData = await fetchConsumerMonitoring(
          token,
          granularity,
          currentSpace?.id || "",
          start,
          end
        );
        setData(newData);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      }
    };
    if (currentSpace && currentSpace.hasSmartMeter) {
      loadData();
    }
  }, [referenceDate, token, currentSpace, setGlobalError]);

  const spaceHasMonitoring = useCallback((space: AutomationSpace): boolean => {
    return space.isProsumer || space.hasSmartMeter;
  }, []);

  const handleNoSpaceAvailable = useCallback((): void => {
    history.push("/onboarding/no-monitoring-credentials");
  }, [history]);

  const handleTabChange = (event, newValue) => {
    let start, end;

    if (newValue === "day") {
      start = TODAY;
    }

    if (newValue === "week") {
      [start, end] = findWeek(TODAY);
    }

    if (newValue === "month") {
      [start, end] = findMonth(TODAY);
    }

    if (newValue === "year") {
      [start, end] = findYear(TODAY);
    }

    setReferenceDate({ start, end, granularity: newValue });
  };

  const handleDateChange = (start: Date, end?: Date) => {
    setReferenceDate((prevState) => ({ ...prevState, start, end }));
  };

  const handleUnitChange = (event, newUnit) => {
    setUnits(newUnit);
  };

  return (
    <PageStandard
      {...PAGE_DATA}
      selectorFilter={spaceHasMonitoring}
      onNoSpaceAvailable={handleNoSpaceAvailable}
    >
      <Tabs
        textColor="secondary"
        value={referenceDate.granularity}
        onChange={handleTabChange}
      >
        {PAGE_TABS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} />
        ))}
      </Tabs>
      <SelectorGranularities
        startDate={referenceDate.start}
        endDate={referenceDate.end}
        onDateChange={handleDateChange}
        granularity={referenceDate.granularity}
      />
      <BlockStandard hideDivider>
        <Grid container item direction="row" gap={2}>
          <Grid container item sx={{ flex: 1 }}>
            <Grid container item sx={{ flex: 1 }}>
              <img
                src="/static/img/solutions/consumption.png"
                alt="Ilustração de um smartmeter"
                style={{ height: "66px" }}
              />
            </Grid>
            <Grid item container direction="column" sx={{ flex: 1 }}>
              <Typography variant="body2" color="primary.main">
                Consumo Total
              </Typography>
              <Typography variant="h2" component="p" color="primary.main">
                {dataOverview?.total
                  ? dataOverview.total[units].toLocaleString(
                      ptBR.code,
                      UNIT_CONFIG[units]
                    )
                  : null}
                {dataOverview?.total && units !== "money" && ` ${units}`}
              </Typography>
            </Grid>
          </Grid>

          <Grid container item justifyContent="space-around" sx={{ flex: 1 }}>
            <ToggleButtonGroup
              fullWidth
              value={units}
              onChange={handleUnitChange}
              exclusive
              sx={{ justifyContent: "space-around" }}
            >
              <ToggleButton className={classes.toggleButton} value="kWh">
                <Typography variant="caption">kWh</Typography>
              </ToggleButton>

              <ToggleButton className={classes.toggleButton} value="money">
                <Typography variant="caption">R$</Typography>
              </ToggleButton>

              <ToggleButton className={classes.toggleButton} value="CO2e">
                <Typography variant="caption">CO2e</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </BlockStandard>

      {data.length > 1 && (
        <BlockStandard hideDivider>
          <Typography>
            Detalhamento de todos os medidores inteligentes
          </Typography>
          <Grid container item gap={2}>
            {data.map(({ name, total }) => (
              <InfoCard
                key={name}
                title={name}
                value={
                  total[units].toLocaleString(ptBR.code, UNIT_CONFIG[units]) ||
                  ""
                }
                loading={!error && !data?.length}
              />
            ))}
          </Grid>
        </BlockStandard>
      )}

      <BlockStandard hideDivider>
        <ChartConsumerMonitoring
          unit={units}
          data={data}
          granularity={referenceDate.granularity}
          isLoading={!error && !data.length}
        />
      </BlockStandard>

      <CustomSnackbar
        message={error}
        open={!!error}
        handleClose={() => setError("")}
      />
    </PageStandard>
  );
};
